import React, { useState, useEffect, useRef } from 'react';
import { Container, Form, Button, Card, Alert, Row, Col, Modal } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import api, { CaseStudy } from '../services/api';
import LocationPicker from './LocationPicker';
import { useAuth } from '../contexts/AuthContext';

const CaseStudyForm: React.FC = () => {
  const { id, userId, projectId } = useParams<{ id?: string; userId?: string; projectId?: string }>();
  const navigate = useNavigate();
  const { tier, tierLimits } = useAuth();
  const caseStudyId = projectId || id;
  const isEdit = !!caseStudyId;

  const [formData, setFormData] = useState({
    projectName: '',
    country: '',
    description: '',
    caseStudyUrl: '',
    latitude: 47,
    longitude: -36,
    hideFromMap: false,
    approximateLocation: false,
  });
  const [caseStudyUrlError, setCaseStudyUrlError] = useState<string | null>(null);
  const [existingPhotos, setExistingPhotos] = useState<any[]>([]);
  // Staged (not-yet-uploaded) photos. Each carries an object URL for the thumbnail so users can
  // stage files across multiple file-picker visits and remove individual ones before saving.
  const [newPhotos, setNewPhotos] = useState<Array<{ file: File; previewUrl: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [locationSelected, setLocationSelected] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [pendingLat, setPendingLat] = useState(47);
  const [pendingLng, setPendingLng] = useState(-36);
  const [mapDefaultZoom, setMapDefaultZoom] = useState<number | undefined>(undefined);

  // Keep a live ref of staged photos so the unmount cleanup below can revoke their object URLs
  // without capturing a stale closure.
  const newPhotosRef = useRef(newPhotos);
  useEffect(() => {
    newPhotosRef.current = newPhotos;
  }, [newPhotos]);

  useEffect(() => {
    return () => {
      newPhotosRef.current.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    };
  }, []);

  useEffect(() => {
    if (isEdit && caseStudyId) {
      loadCaseStudy();
    } else if (userId) {
      // Seed new-record form with the user's most recently tagged location at country zoom
      api.getLastLocation(userId).then((last) => {
        if (last) {
          setFormData((prev) => ({ ...prev, latitude: last.lat, longitude: last.lng }));
          setMapDefaultZoom(5);
        }
      }).catch(() => { /* non-fatal */ });
    }
  }, [caseStudyId, isEdit, userId]);

  const loadCaseStudy = async () => {
    try {
      const caseStudy = await api.getCaseStudy(caseStudyId!, userId);
      setFormData({
        projectName: caseStudy.projectName,
        country: caseStudy.country || '',
        description: caseStudy.description,
        caseStudyUrl: caseStudy.caseStudyUrl || '',
        latitude: caseStudy.location.coordinates[1],
        longitude: caseStudy.location.coordinates[0],
        hideFromMap: caseStudy.hideFromMap || false,
        approximateLocation: caseStudy.approximateLocation || false,
      });
      setExistingPhotos(caseStudy.photos);
      setLocationSelected(true);
    } catch (err) {
      setError('Failed to load case study');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resizeImage = (file: File, maxDimension = 1920): Promise<File> => {
    return new Promise((resolve) => {
      if (!file.type.startsWith('image/')) {
        resolve(file);
        return;
      }

      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);

        const { width, height } = img;
        if (width <= maxDimension && height <= maxDimension) {
          resolve(file);
          return;
        }

        const scale = maxDimension / Math.max(width, height);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(width * scale);
        canvas.height = Math.round(height * scale);

        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(new File([blob], file.name, { type: 'image/jpeg' }));
            } else {
              resolve(file);
            }
          },
          'image/jpeg',
          0.85
        );
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(file);
      };

      img.src = url;
    });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    if (!input.files?.length) return;

    const files = Array.from(input.files);
    const maxPhotos = tierLimits.maxPhotos;
    const remainingSlots = maxPhotos - existingPhotos.length - newPhotos.length;

    const filesToProcess = files.length > remainingSlots ? files.slice(0, remainingSlots) : files;

    if (files.length > remainingSlots) {
      setError(`You can only add ${remainingSlots} more photo(s). ${tier === 'free' ? 'Upgrade for more.' : ''}`);
    } else {
      setError(null);
    }

    const resized = await Promise.all(filesToProcess.map((f) => resizeImage(f)));
    const staged = resized.map((file) => ({ file, previewUrl: URL.createObjectURL(file) }));
    setNewPhotos((prev) => [...prev, ...staged]);

    // Reset so re-picking the same file (after removing it) re-triggers onChange.
    input.value = '';
  };

  const removePendingPhoto = (index: number) => {
    setNewPhotos((prev) => {
      const target = prev[index];
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const openMapModal = () => {
    setPendingLat(formData.latitude);
    setPendingLng(formData.longitude);
    setShowMapModal(true);
  };

  const confirmLocation = () => {
    setFormData((prev) => ({ ...prev, latitude: pendingLat, longitude: pendingLng }));
    setLocationSelected(true);
    setShowMapModal(false);
  };

  const removeExistingPhoto = (index: number) => {
    setExistingPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDelete = async () => {
    if (!caseStudyId) return;
    if (!window.confirm('Are you sure you want to delete this case study? This cannot be undone.')) return;

    setLoading(true);
    setError(null);
    try {
      await api.deleteCaseStudy(caseStudyId, userId);
      navigate(userId ? `/${userId}/admin` : '/admin');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete case study');
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.hideFromMap && !locationSelected) {
      setError('Please select a location on the map before saving.');
      return;
    }
    if (caseStudyUrlError) {
      setError('Please fix the Case Study URL before saving.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('projectName', formData.projectName);
      formDataToSend.append('country', formData.country);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('caseStudyUrl', formData.caseStudyUrl.trim());
      formDataToSend.append('latitude', formData.latitude.toString());
      formDataToSend.append('longitude', formData.longitude.toString());
      formDataToSend.append('hideFromMap', formData.hideFromMap.toString());
      formDataToSend.append('approximateLocation', formData.approximateLocation.toString());

      if (isEdit && existingPhotos.length > 0) {
        formDataToSend.append('existingPhotos', JSON.stringify(existingPhotos));
      }

      newPhotos.forEach((pending) => {
        formDataToSend.append('photos', pending.file);
      });

      if (isEdit && caseStudyId) {
        await api.updateCaseStudy(caseStudyId, formDataToSend, userId);
      } else {
        await api.createCaseStudy(formDataToSend, userId);
      }

      const adminUrl = userId ? `/${userId}/admin` : '/';
      navigate(adminUrl);
    } catch (err: any) {
      setError(err.response?.data?.error || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="mt-5">
      <Card>
        <Card.Header>
          <h2>{isEdit ? 'Edit Record' : 'New Record'}</h2>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form onSubmit={handleSubmit}>
            <div className="d-flex gap-2">
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
              </Button>
              <Button variant="secondary" onClick={() => navigate(`/${userId}/admin`)}>
                Cancel
              </Button>
            </div>
            <br/>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Project Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="projectName"
                    value={formData.projectName}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Country (Optional)</Form.Label>
                  <Form.Control
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description <span className="text-danger">*</span></Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                maxLength={10000}
                required
              />
              <Form.Text className={formData.description.length > 9500 ? 'text-warning' : 'text-muted'}>
                {formData.description.length.toLocaleString()}/10,000 characters
              </Form.Text>
              <br />
              <Form.Text className="text-muted">
                Tip: Use **text** for bold. Blank lines create paragraphs.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Case Study URL (optional)</Form.Label>
              <Form.Control
                type="url"
                name="caseStudyUrl"
                value={formData.caseStudyUrl}
                onChange={(e) => {
                  if (caseStudyUrlError) setCaseStudyUrlError(null);
                  handleInputChange(e);
                }}
                onBlur={() => {
                  const v = formData.caseStudyUrl.trim();
                  if (!v) { setCaseStudyUrlError(null); return; }
                  try {
                    const u = new URL(v);
                    setCaseStudyUrlError(
                      u.protocol === 'http:' || u.protocol === 'https:'
                        ? null
                        : 'Must start with http:// or https://'
                    );
                  } catch {
                    setCaseStudyUrlError('Enter a valid URL (e.g. https://example.com/...)');
                  }
                }}
                placeholder="https://yoursite.com/projects/warehouse-solar"
                isInvalid={!!caseStudyUrlError}
              />
              <Form.Control.Feedback type="invalid">
                {caseStudyUrlError}
              </Form.Control.Feedback>
              <Form.Text className="text-muted">
                If you have an existing case study page, paste the link here. Map markers will open it directly instead of showing a summary page.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Photos</Form.Label>
              {existingPhotos.length > 0 && (
                <div className="mb-2">
                  <small>Existing photos:</small>
                  <div className="d-flex flex-wrap gap-2 mt-1">
                    {existingPhotos.map((photo, index) => (
                      <div key={index} className="position-relative">
                        <img
                          src={photo.url}
                          alt={`Photo ${index + 1}`}
                          style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                        />
                        <Button
                          variant="danger"
                          size="sm"
                          className="position-absolute top-0 end-0"
                          onClick={() => removeExistingPhoto(index)}
                        >
                          ×
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {newPhotos.length > 0 && (
                <div className="mb-2">
                  <small>Photos to upload (not yet saved):</small>
                  <div className="d-flex flex-wrap gap-2 mt-1">
                    {newPhotos.map((pending, index) => (
                      <div key={pending.previewUrl} className="position-relative">
                        <img
                          src={pending.previewUrl}
                          alt={`Pending photo ${index + 1}`}
                          style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                        />
                        <Button
                          variant="danger"
                          size="sm"
                          className="position-absolute top-0 end-0"
                          onClick={() => removePendingPhoto(index)}
                        >
                          ×
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <Form.Control
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileChange}
              />
              <Form.Text className="text-muted">
                {tier === 'free'
                  ? `Photos: ${existingPhotos.length + newPhotos.length}/${tierLimits.maxPhotos} (Free tier limit). `
                  : `You can upload up to ${tierLimits.maxPhotos} photos. `}
                Click again to add more photos from a different folder — nothing uploads until you save.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Location</Form.Label>
              <Form.Check
                type="checkbox"
                label="Hide location from public map — this project will not show a pin on the map"
                checked={formData.hideFromMap}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, hideFromMap: e.target.checked }))
                }
                className="mb-2"
              />
              {!formData.hideFromMap && (
                <>
                  <Form.Check
                    type="checkbox"
                    label='Mark as "Location is approximate"'
                    checked={formData.approximateLocation}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, approximateLocation: e.target.checked }))
                    }
                    className="mb-2"
                  />
                  {locationSelected ? (
                    <div className="d-flex align-items-start gap-3 flex-wrap">
                      <div
                        style={{ width: '200px', height: '150px', flexShrink: 0 }}
                        className="border rounded overflow-hidden"
                      >
                        <LocationPicker
                          lat={formData.latitude}
                          lng={formData.longitude}
                          onLocationChange={() => {}}
                          defaultZoom={8}
                          height="150px"
                          readOnly
                        />
                      </div>
                      <div>
                        <Button variant="outline-primary" onClick={openMapModal}>
                          Change Location
                        </Button>
                        <div className="text-muted small mt-2">
                          {formData.latitude.toFixed(5)}, {formData.longitude.toFixed(5)}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="mb-2">
                        <Button variant="outline-primary" onClick={openMapModal}>
                          Select Location on Map
                        </Button>
                      </div>
                      <div className="text-muted small">No location selected yet</div>
                    </>
                  )}
                </>
              )}
            </Form.Group>

            <Modal
              show={showMapModal}
              onHide={() => setShowMapModal(false)}
              size="lg"
              fullscreen="sm-down"
              backdrop="static"
              centered
            >
              <Modal.Header closeButton>
                <Modal.Title>Select Location</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <LocationPicker
                  lat={pendingLat}
                  lng={pendingLng}
                  onLocationChange={(lat, lng) => {
                    setPendingLat(lat);
                    setPendingLng(lng);
                  }}
                  defaultZoom={mapDefaultZoom}
                  gestureHandling="greedy"
                  height="60vh"
                />
                <Row className="mt-2">
                  <Col>
                    <Form.Control
                      type="number"
                      step="any"
                      placeholder="Latitude"
                      value={pendingLat}
                      onChange={(e) => setPendingLat(parseFloat(e.target.value) || 0)}
                    />
                  </Col>
                  <Col>
                    <Form.Control
                      type="number"
                      step="any"
                      placeholder="Longitude"
                      value={pendingLng}
                      onChange={(e) => setPendingLng(parseFloat(e.target.value) || 0)}
                    />
                  </Col>
                </Row>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowMapModal(false)}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={confirmLocation}>
                  Done
                </Button>
              </Modal.Footer>
            </Modal>

            {error && <Alert variant="danger">{error}</Alert>}
            <div className="d-flex justify-content-between flex-wrap gap-2">
              <div className="d-flex gap-2">
                <Button variant="primary" type="submit" disabled={loading}>
                  {loading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
                </Button>
                <Button variant="secondary" onClick={() => navigate(`/${userId}/admin`)}>
                  Cancel
                </Button>
              </div>
              {isEdit && (
                <Button variant="outline-danger" onClick={handleDelete} disabled={loading}>
                  Delete Record
                </Button>
              )}
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default CaseStudyForm;